import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { PieChart, pieClasses } from '@mui/x-charts';
import { HighlightItemData } from '@mui/x-charts/context';
import { useLocalization } from '@/hooks/useLocalization';
import { mobileWidth } from '@/constants';
import { formatNumberToCurrency } from '@/utils/currency';
import { useGraphColors } from '@/hooks/useGraphColors';
import {
  getCollapsedPieItems,
  GroupedOther,
  PieGroupItem,
  PieInputItem,
  GroupedItem,
  usePieGrouping,
} from './usePieGrouping';

/** Type-guard: returns true when a grouped entry is the aggregated "Other" bucket. */
function isOtherGroup<T>(g: GroupedItem<T>): g is GroupedOther<T> {
  return 'isOtherGroup' in g && g.isOtherGroup;
}

interface PieChartWithLegendProps<T = unknown> {
  items: PieInputItem<T>[];
  thresholdPercent?: number;
  width?: number;
  height?: number;
  outerRadius?: number;
  innerRadius?: number;
  paddingAngle?: number;
  cornerRadius?: number;
  arcLabelMinAngle?: number;
  seriesId?: string;
  highlightedItem?: HighlightItemData | null;
  onHighlightChange?: (item: HighlightItemData | null) => void;
  onSliceClick?: (item: PieGroupItem<T>) => void;
  colorResolver?: (index: number) => string;
  /** Slice labels that stay visible when they are under `thresholdPercent`. */
  keepVisibleLabels?: readonly string[];
}

export function PieChartWithLegend<T = unknown>({
  items,
  thresholdPercent = 2,
  width = 350,
  height = 350,
  outerRadius = 140,
  innerRadius = 0,
  paddingAngle = 1,
  cornerRadius = 5,
  arcLabelMinAngle = 25,
  seriesId = 'pie',
  highlightedItem,
  onHighlightChange,
  onSliceClick,
  colorResolver,
  keepVisibleLabels,
}: PieChartWithLegendProps<T>) {
  const graphColors = useGraphColors();
  const resolver = useMemo(
    () => colorResolver ?? ((i: number) => graphColors[i % graphColors.length]),
    [colorResolver, graphColors],
  );

  const { t } = useLocalization();
  const isMobileMatch = useMediaQuery(mobileWidth);
  const [expandedOther, setExpandedOther] = useState(false);
  const [internalHighlight, setInternalHighlight] =
    useState<HighlightItemData | null>(null);

  const { total, grouped, hasOtherGroup } = usePieGrouping(
    items,
    thresholdPercent,
    resolver,
    { keepVisibleLabels },
  );
  const otherGroup = useMemo(() => grouped.find(isOtherGroup), [grouped]);

  useEffect(() => {
    // Reset "Other" expansion and any highlight when the dataset changes.
    // Also prevents a slice (often the first, e.g. "Equities") from looking
    // highlighted by default on mount due to chart-internal highlight events.
    setExpandedOther(false);
    setInternalHighlight(null);
    onHighlightChange?.(null);
  }, [items, onHighlightChange]);

  useEffect(() => {
    // Toggling the "Other" expansion swaps the rendered slice set, so a leftover
    // highlight from the previous set would point at a now-wrong/absent dataIndex
    // and (with fade: 'global') gray out the entire pie. Clear it on every toggle.
    setInternalHighlight(null);
    onHighlightChange?.(null);
  }, [expandedOther, onHighlightChange]);

  const renderedItems = useMemo<PieGroupItem<T>[]>(() => {
    if (expandedOther) {
      const other = grouped.find(isOtherGroup);
      if (other) {
        const getRawTotal = (i: PieGroupItem<T>): number => {
          const original = i.original as { total?: number } | undefined;
          return typeof original?.total === 'number'
            ? original.total
            : i.amount;
        };
        return [...other.subItems].sort(
          (a, b) => getRawTotal(b) - getRawTotal(a),
        );
      }
      // Fallback to non-Other items
      return grouped.filter((g): g is PieGroupItem<T> => !isOtherGroup(g));
    }

    return getCollapsedPieItems(grouped);
  }, [expandedOther, grouped]);

  const chartData = useMemo(
    () =>
      renderedItems.map((item, index) => {
        // The collapsed aggregate is appended last by getCollapsedPieItems, so
        // resolve it positionally rather than by label — a real category named
        // "Other" would otherwise be mistaken for it. Its tooltip mirrors the
        // legend wording instead of the bare "Other" bucket name.
        const isCollapsedOther =
          !expandedOther && hasOtherGroup && index === renderedItems.length - 1;
        return {
          // Use a positional id rather than the label so duplicate labels (e.g. an
          // input category literally named "Other" plus the generated aggregate
          // "Other" bucket) don't collide into a non-unique series id.
          id: `${seriesId}-${String(index)}`,
          value: item.amount,
          label: isCollapsedOther
            ? t('legend.expandOther', { threshold: thresholdPercent })
            : item.label,
          color: item.color,
        };
      }),
    [
      renderedItems,
      seriesId,
      expandedOther,
      hasOtherGroup,
      t,
      thresholdPercent,
    ],
  );

  const currentHighlighted = highlightedItem ?? internalHighlight;
  const setHighlight =
    onHighlightChange ??
    ((value: HighlightItemData | null) => {
      setInternalHighlight(value);
    });

  // Resolve a legend row to its index in the rendered pie data without matching
  // by display text: duplicate labels (e.g. an input category named "Other"
  // above the threshold plus the generated aggregate "Other" bucket) would make
  // a label lookup highlight the wrong slice. Legend rows share object identity
  // with the rendered slices they represent, so a reference lookup is unique.
  const resolveLegendDataIndex = (
    item: PieGroupItem<T> | GroupedOther<T>,
  ): number => {
    if (!expandedOther && isOtherGroup(item)) {
      // The collapsed aggregate "Other" slice is rebuilt as a fresh object by
      // getCollapsedPieItems and appended last, so it has no shared reference;
      // resolve it positionally as the final rendered slice.
      return renderedItems.length - 1;
    }
    return renderedItems.indexOf(item);
  };

  const handleLegendHover = (dataIndex: number | null) => {
    if (dataIndex === null || dataIndex < 0) {
      setHighlight(null);
      return;
    }
    setHighlight({ seriesId, dataIndex });
  };

  const handlePieClick = (dataIndex: number | null) => {
    if (dataIndex === null) return;
    const clicked = renderedItems[dataIndex];
    const isOtherCollapsed =
      !expandedOther && grouped.some((g) => isOtherGroup(g) && g.label);
    if (isOtherCollapsed && clicked.label === otherGroup?.label) {
      setExpandedOther(true);
      return;
    }
    onSliceClick?.(clicked);
  };

  const legendItems: Array<PieGroupItem<T> | GroupedOther<T>> = useMemo(() => {
    if (!expandedOther) return grouped;
    const other = grouped.find(isOtherGroup);
    if (!other) return grouped;
    if (seriesId === 'liabilities') {
      const getRawTotal = (i: PieGroupItem<T>): number => {
        const original = i.original as { total?: number } | undefined;
        return typeof original?.total === 'number' ? original.total : i.amount;
      };
      return [...other.subItems].sort(
        (a, b) => getRawTotal(b) - getRawTotal(a),
      );
    }
    return other.subItems;
  }, [grouped, expandedOther, seriesId]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
    >
      <Box
        height={height}
        width={width}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          // Target the arc-label text via the seriesLabels group (that group
          // holds only the on-slice labels); pieArcLabelClasses is deprecated.
          [`& .${pieClasses.seriesLabels} text`]: {
            fill: 'white',
            fontSize: 14,
            fontWeight: 'bold',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          },
          '& .MuiChartsLegend-root': {
            display: 'none',
          },
        }}
        onMouseLeave={() => {
          setHighlight(null);
        }}
      >
        <PieChart
          height={height}
          width={width}
          series={[
            {
              id: seriesId,
              data: chartData,
              arcLabel: (item) => {
                const value = typeof item.value === 'number' ? item.value : 0;
                const percent = total > 0 ? (value / total) * 100 : 0;
                if (
                  otherGroup &&
                  item.label === otherGroup.label &&
                  percent >= thresholdPercent
                ) {
                  // Show percentage for the aggregated Other slice
                  return `${percent.toFixed(2)}%`;
                }
                return total > 0 ? `${percent.toFixed(2)}%` : '0.00%';
              },
              arcLabelMinAngle,
              highlightScope: { highlight: 'item', fade: 'global' },
              valueFormatter: (valueObj) => {
                const v =
                  typeof valueObj === 'number'
                    ? valueObj
                    : typeof valueObj.value === 'number'
                      ? valueObj.value
                      : 0;
                // Show two decimal places on tooltip values
                return formatNumberToCurrency(v, undefined, 2);
              },
              outerRadius,
              innerRadius,
              paddingAngle,
              cornerRadius,
            },
          ]}
          highlightedItem={currentHighlighted ?? null}
          onHighlightChange={setHighlight}
          onItemClick={(_event, d) => {
            handlePieClick(d.dataIndex);
          }}
        />
      </Box>

      {/* chart-print-safe forces dark text for print — without it the legend
          prints white-on-white under the dark theme (see index.css). Scoped to
          the legend so the white in-slice percentages keep their contrast. */}
      <Box
        className='chart-print-safe'
        display='flex'
        flexDirection='column'
        mt={2}
        width='100%'
      >
        {expandedOther && (
          <Button
            onClick={() => {
              setExpandedOther(false);
            }}
            sx={{ mb: 1, displayPrint: 'none' }}
          >
            {t('legend.collapseOther', { threshold: thresholdPercent })}
          </Button>
        )}
        {legendItems.map((item, index) => {
          const itemIsOther = isOtherGroup(item);
          const isExpandable = itemIsOther && !expandedOther && hasOtherGroup;

          const labelText = isExpandable
            ? isMobileMatch
              ? t('legend.otherShort', { threshold: thresholdPercent })
              : t('legend.expandOther', { threshold: thresholdPercent })
            : item.label;

          const amount = item.amount;
          const percent =
            total > 0 ? ((amount / total) * 100).toFixed(2) : '0.00';

          const isLiabilitiesSeries = seriesId === 'liabilities';
          const getRawTotal = (i: PieGroupItem<T>): number => {
            const original = i.original as { total?: number } | undefined;
            return typeof original?.total === 'number'
              ? original.total
              : i.amount;
          };

          let currencyValue = amount;
          if (isLiabilitiesSeries) {
            if (itemIsOther && !expandedOther && otherGroup) {
              currencyValue = otherGroup.subItems.reduce(
                (sum, sub) => sum + getRawTotal(sub),
                0,
              );
            } else if (!itemIsOther) {
              currencyValue = getRawTotal(item);
            }
          }

          return (
            <Box
              key={`${seriesId}-legend-${String(index)}-${labelText}`}
              sx={{ borderBottom: 1, borderColor: 'divider', paddingY: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 3,
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  textAlign: 'left',
                  maxWidth: 500,
                  borderRadius: 1,
                }}
                onClick={() => {
                  if (isExpandable) {
                    setExpandedOther(true);
                  } else if (!itemIsOther) {
                    // When expanded, legend shows only sub-items; when collapsed, non-Other items are main slices
                    handlePieClick(index);
                  } else {
                    // Click on collapsed "Other" triggers expand
                    setExpandedOther(true);
                  }
                }}
                onMouseEnter={() => {
                  handleLegendHover(resolveLegendDataIndex(item));
                }}
                onMouseLeave={() => {
                  handleLegendHover(null);
                }}
              >
                <Box sx={{ display: 'flex' }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      m: '6px',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  />

                  <Typography>{labelText}</Typography>
                </Box>

                <Typography
                  component='div'
                  sx={{ flexShrink: 0, textAlign: 'right' }}
                >
                  {formatNumberToCurrency(currencyValue)}
                  <Typography
                    component='span'
                    color='text.secondary'
                    fontSize='small'
                    sx={{ display: 'block' }}
                  >
                    ({percent}%)
                  </Typography>
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default PieChartWithLegend;
